import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { brand } from "@/lib/brand";
import { useRealtime } from "@/hooks/useRealtime";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const NotificationBell = ({ testid = "notification-bell" }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0, total: 0 });
  const [size, setSize] = useState(20);
  const load = useCallback(() => { api.get("/notifications", { params: { page: 1, page_size: size } }).then((r) => setData(r.data)).catch(() => {}); }, [size]);
  useEffect(() => { load(); }, [load]);
  useRealtime((ev) => { if (ev.type === "notification") load(); });

  const openItem = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`);
    setOpen(false);
    load();
    if (n.link) navigate(n.link);
  };
  const readAll = async () => { await api.post("/notifications/read-all"); load(); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative grid h-9 w-9 place-items-center rounded-xl border border-slate-200 bg-white transition-colors hover:bg-[#F5F7FA]" data-testid={testid} aria-label="Notificaciones">
          <Bell className="h-4 w-4 text-[#0B2545]" />
          {data.unread > 0 && (
            <span className="absolute -right-1.5 -top-1.5 grid h-5 min-w-5 place-items-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white" data-testid={`${testid}-count`}>
              {data.unread > 99 ? "99+" : data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 rounded-2xl p-0" data-testid="notifications-panel">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <p className="text-sm font-bold">Notificaciones</p>
          <button onClick={readAll} disabled={!data.unread} className="flex items-center gap-1 text-xs text-[#1E6FD9] disabled:text-slate-300" data-testid="notifications-read-all">
            <CheckCheck className="h-3.5 w-3.5" /> Marcar todas como leídas
          </button>
        </div>
        <ul className="max-h-96 overflow-auto">
          {data.items.length === 0 && <li className="px-4 py-10 text-center text-sm text-slate-400" data-testid="notifications-empty">Sin notificaciones</li>}
          {data.items.map((n) => (
            <li key={n.id} className={`flex gap-2 border-b border-slate-50 px-4 py-3 ${n.read ? "" : "bg-[#1E6FD9]/[0.04]"}`} data-testid={`notification-item-${n.id}`}>
              <button onClick={() => openItem(n)} className="min-w-0 flex-1 text-left">
                <p className={`text-sm ${n.read ? "text-slate-600" : "font-semibold text-[#0B2545]"}`}>{brand(n.title)}</p>
                <p className="text-[11px] text-slate-400">{fmtDateTime(n.created_at)} · {n.actor_name}</p>
              </button>
              {!n.read && (
                <button onClick={async () => { await api.post(`/notifications/${n.id}/read`); load(); }} className="shrink-0 text-[11px] text-[#1E6FD9]"
                        data-testid={`notification-mark-read-${n.id}`}>Marcar como leída</button>
              )}
            </li>
          ))}
        </ul>
        {data.items.length < data.total && (
          <button onClick={() => setSize((s) => s + 20)} className="w-full py-2.5 text-xs font-semibold text-[#1E6FD9]" data-testid="notifications-load-more">Ver más</button>
        )}
      </PopoverContent>
    </Popover>
  );
};
